/**
 * CalculationModePicker - "How is this card scored?"
 *
 * The stored mode names are the data model's words. Someone setting up an
 * audit score is asking "against what?" - a fixed target, a budget they should
 * land close to, or a baseline they want to beat - so the options are named by
 * that question instead.
 *
 * Each option carries a diagram of its own band shape, which is the cheapest
 * way to make the mirrored symmetry of the +/-% mode visible before you commit
 * to it rather than after.
 */
import type { Level, LevelCalculationMode } from '../api/types';
import { dashboardAssuranceColors, type AssuranceLevel } from '../utils/color';
import { useChartTilesEnabled } from '../hooks/useDashboardTileStyle';

interface CalculationModePickerProps {
  value: LevelCalculationMode;
  onChange: (mode: LevelCalculationMode) => void;
  disabled?: boolean;
}

/** Segments as [start, end, level] over a 44-unit wide diagram. */
type Shape = [number, number, Level][];

interface ModeOption {
  value: LevelCalculationMode;
  name: string;
  description: string;
  shape: Shape;
  /** Draws the base as a rule through the diagram. */
  showBase: boolean;
}

const MODE_OPTIONS: ModeOption[] = [
  {
    value: 'absolute',
    name: 'Against a fixed target',
    description: 'The value is compared straight to the numbers you set.',
    shape: [
      [0, 8, 'none'],
      [8, 16, 'limited'],
      [16, 25, 'moderate'],
      [25, 34, 'substantial'],
      [34, 44, 'high']
    ],
    showBase: false
  },
  {
    value: 'deviation_bands',
    name: 'Close to a base value',
    description: 'For budgets and plans, over and under are equally off target.',
    shape: [
      [0, 6, 'none'],
      [6, 11, 'limited'],
      [11, 17, 'moderate'],
      [17, 20, 'substantial'],
      [20, 24, 'high'],
      [24, 27, 'substantial'],
      [27, 33, 'moderate'],
      [33, 38, 'limited'],
      [38, 44, 'none']
    ],
    showBase: true
  },
  {
    value: 'deviation_direct',
    name: 'As a share of a base value',
    description: 'Scored on percent of base, so you can reward beating it or staying under.',
    shape: [
      [0, 9, 'none'],
      [9, 17, 'limited'],
      [17, 25, 'moderate'],
      [25, 33, 'substantial'],
      [33, 44, 'high']
    ],
    showBase: true
  }
];

function toAssuranceLevel(level: Level): AssuranceLevel {
  return (level.charAt(0).toUpperCase() + level.slice(1)) as AssuranceLevel;
}

export default function CalculationModePicker({ value, onChange, disabled = false }: CalculationModePickerProps) {
  const useChartTiles = useChartTilesEnabled();
  const palette = dashboardAssuranceColors(useChartTiles);

  // No heading of its own — the step it sits in supplies the question.
  return (
    <div className="flex flex-col gap-2">
      {MODE_OPTIONS.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            aria-pressed={selected}
            aria-label={`${option.name}. ${option.description}`}
            onClick={() => onChange(option.value)}
            className={`w-full px-4 py-3 rounded-2xl flex flex-col text-left items-start transition-all relative ${
              selected ? 'border-2 border-border-active bg-surface-hover' : 'border border-gray-300 bg-white'
            } ${disabled ? 'cursor-not-allowed opacity-60' : selected ? 'cursor-pointer' : 'cursor-pointer hover:bg-blue-200/20'}`}
          >
            <div className="flex flex-col gap-1 w-full">
              <div className="flex items-center gap-2 w-full">
                <svg width="44" height="30" viewBox="0 0 44 30" aria-hidden="true" className="flex-shrink-0">
                  {option.shape.map(([start, end, level], index) => (
                    <rect
                      key={index}
                      x={start}
                      y={9}
                      width={end - start}
                      height={12}
                      fill={palette[toAssuranceLevel(level)]}
                    />
                  ))}
                  {option.showBase && <line x1="22" y1="4" x2="22" y2="26" stroke="#ffffff" strokeWidth="1.4" />}
                </svg>
                <span className="text-base font-semibold text-content-default">{option.name}</span>
              </div>
              <span className="text-content-subtle text-sm">{option.description}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
